import React, { useState, useEffect, useCallback } from 'react';
import trustedDevicesService from '../../../services/trustedDevicesService';

const TrustedDevicesTab = () => {
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, blocked: 0 });
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('pending');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [nameValue, setNameValue] = useState('');
  const [approveModal, setApproveModal] = useState(null); // { deviceId, userLogin }
  const [approveExpires, setApproveExpires] = useState(''); // datetime-local value
  const [approvePermanent, setApprovePermanent] = useState(true);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trustedDevicesService.getDevices();
      if (res.success) {
        setDevices(res.data.devices || []);
        setStats(res.data.stats || { pending: 0, approved: 0, blocked: 0 });
      }
    } catch (e) {
      setErrorMsg('Błąd ładowania urządzeń');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttempts = useCallback(async () => {
    try {
      const res = await trustedDevicesService.getLoginAttempts({ limit: 200 });
      if (res.success) {
        setLoginAttempts(res.data.attempts || []);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadDevices();
    loadAttempts();
    // Auto-odświeżanie co 30s (wygasłe zatwierdzenia, nowe urządzenia)
    const interval = setInterval(() => { loadDevices(); loadAttempts(); }, 30000);
    return () => clearInterval(interval);
  }, [loadDevices, loadAttempts]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const openApproveModal = (deviceId, userLogin) => {
    setApproveModal({ deviceId, userLogin });
    setApprovePermanent(true);
    setApproveExpires('');
  };

  const handleApproveConfirm = async () => {
    if (!approveModal) return;
    const expiresAt = approvePermanent ? null : (approveExpires || null);
    if (!approvePermanent && !approveExpires) {
      setErrorMsg('Wybierz datę i godzinę wygaśnięcia');
      return;
    }
    try {
      const res = await trustedDevicesService.approveDevice(approveModal.deviceId, expiresAt);
      if (res.success) {
        showSuccess(res.message);
        setApproveModal(null);
        loadDevices();
      }
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleBlock = async (deviceId) => {
    if (!window.confirm('Czy na pewno chcesz zablokować to urządzenie?')) return;
    try {
      const res = await trustedDevicesService.blockDevice(deviceId);
      if (res.success) {
        showSuccess(res.message);
        loadDevices();
      }
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('Usunięcie urządzenia wymusi ponowne zatwierdzenie. Kontynuować?')) return;
    try {
      const res = await trustedDevicesService.deleteDevice(deviceId);
      if (res.success) {
        showSuccess(res.message);
        loadDevices();
      }
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleSaveName = async (deviceId) => {
    try {
      await trustedDevicesService.updateDeviceName(deviceId, nameValue);
      setEditingName(null);
      setNameValue('');
      loadDevices();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const parseUA = (ua) => {
    if (!ua) return 'Nieznane';
    let browser = 'Przeglądarka';
    let os = '';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    return `${browser}${os ? ' / ' + os : ''}`;
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: '#fff3cd', color: '#856404', text: 'Oczekuje' },
      approved: { bg: '#d4edda', color: '#155724', text: 'Zatwierdzone' },
      blocked: { bg: '#f8d7da', color: '#721c24', text: 'Zablokowane' }
    };
    const s = map[status] || map.pending;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 600, backgroundColor: s.bg, color: s.color
      }}>
        {s.text}
      </span>
    );
  };

  const attemptBadge = (type) => {
    const map = {
      success: { bg: '#d4edda', color: '#155724', text: 'Sukces' },
      blocked_device: { bg: '#fff3cd', color: '#856404', text: 'Zablokowane' },
      bad_password: { bg: '#f8d7da', color: '#721c24', text: 'Złe hasło' },
      unknown_user: { bg: '#f8d7da', color: '#721c24', text: 'Nieznany user' }
    };
    const s = map[type] || { bg: '#e2e3e5', color: '#383d41', text: type };
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 600, backgroundColor: s.bg, color: s.color
      }}>
        {s.text}
      </span>
    );
  };

  const filteredDevices = activeView === 'pending'
    ? devices.filter(d => d.status === 'pending')
    : devices;

  const btnStyle = (color = 'var(--success)') => ({
    padding: '4px 10px', fontSize: '11px', fontWeight: 600,
    border: 'none', cursor: 'pointer',
    color: '#fff', backgroundColor: color
  });

  const cardStyle = {
    backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
    borderRadius: '8px', padding: '1rem'
  };

  const thStyle = {
    padding: '8px 10px', textAlign: 'left', fontSize: '11px',
    fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)',
    borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '8px 10px', fontSize: '12px', color: 'var(--text)',
    borderBottom: '1px solid var(--border)', verticalAlign: 'middle'
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Komunikaty */}
      {successMsg && (
        <div style={{ padding: '10px 14px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '6px', fontSize: '13px' }}>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ padding: '10px 14px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }} onClick={() => setErrorMsg('')}>
          {errorMsg}
        </div>
      )}

      {/* Karty statystyk */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
        <div style={{ ...cardStyle, borderLeft: '4px solid #ffc107' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Oczekujące</div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#856404' }}>{stats.pending}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #28a745' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Zatwierdzone</div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#155724' }}>{stats.approved}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #dc3545' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Zablokowane</div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#721c24' }}>{stats.blocked}</div>
        </div>
      </div>

      {/* Zakładki */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
        {[
          { id: 'pending', label: 'Oczekujące', count: stats.pending },
          { id: 'all', label: 'Wszystkie urządzenia' },
          { id: 'attempts', label: 'Historia logowań' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveView(tab.id)} style={{
            padding: '6px 12px', fontSize: '12px', fontWeight: 600,
            border: 'none', borderBottom: activeView === tab.id ? '2px solid var(--success)' : '2px solid transparent',
            backgroundColor: activeView === tab.id ? 'var(--success-light)' : 'transparent', cursor: 'pointer',
            color: activeView === tab.id ? 'var(--success)' : 'var(--text-secondary)',
            marginBottom: '-2px'
          }}>
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                marginLeft: '6px', padding: '1px 6px', borderRadius: '10px',
                fontSize: '10px', fontWeight: 700, backgroundColor: 'var(--warning-light)', color: 'var(--warning)'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Widok: Urządzenia */}
      {activeView !== 'attempts' && (
        <div style={cardStyle}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ładowanie...</div>
          ) : filteredDevices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '13px' }}>
              {activeView === 'pending' ? 'Brak urządzeń oczekujących na zatwierdzenie' : 'Brak zarejestrowanych urządzeń'}
            </div>
          ) : (
            <div className="admin-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Użytkownik</th>
                    <th style={thStyle}>Nazwa / Token</th>
                    <th className="col-hide-mobile" style={thStyle}>Urządzenie</th>
                    <th className="col-hide-mobile" style={thStyle}>IP</th>
                    <th style={thStyle}>Status</th>
                    <th className="col-hide-mobile" style={thStyle}>Utworzono</th>
                    <th style={thStyle}>Wygasa</th>
                    <th style={thStyle}>Ostatnie logowanie</th>
                    <th style={thStyle}>Logowania</th>
                    <th style={thStyle}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map(d => (
                    <tr key={d.id} style={{ backgroundColor: d.status === 'pending' ? '#fffbe6' : 'transparent' }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{d.user_login}</td>
                      <td style={tdStyle}>
                        {editingName === d.id ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input
                              value={nameValue}
                              onChange={e => setNameValue(e.target.value)}
                              placeholder="Nazwa urządzenia"
                              style={{ padding: '2px 6px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '3px', width: '120px' }}
                              maxLength={100}
                            />
                            <button onClick={() => handleSaveName(d.id)} style={btnStyle('var(--success)')}>OK</button>
                            <button onClick={() => setEditingName(null)} style={btnStyle('var(--text-muted)')}>X</button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: d.device_name ? 600 : 400, fontSize: '12px' }}>
                              {d.device_name || (
                                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', cursor: 'pointer' }}
                                  onClick={() => { setEditingName(d.id); setNameValue(d.device_name || ''); }}>
                                  Nadaj nazwę
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                              {d.device_token ? d.device_token.substring(0, 12) + '...' : '-'}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontSize: '11px' }}>{parseUA(d.user_agent)}</td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px' }}>{d.ip_address || '-'}</td>
                      <td style={tdStyle}>{statusBadge(d.status)}</td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontSize: '11px', whiteSpace: 'nowrap' }}>{formatDate(d.created_at)}</td>
                      <td style={{ ...tdStyle, fontSize: '11px', whiteSpace: 'nowrap' }}>
                        {d.expires_at ? (
                          <span style={{ color: new Date(d.expires_at) < new Date() ? '#dc3545' : '#856404' }}>
                            {formatDate(d.expires_at)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Na stale</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '11px', whiteSpace: 'nowrap' }}>{formatDate(d.last_login_at)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{d.login_count || 0}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {d.status === 'pending' && (
                            <button onClick={() => openApproveModal(d.id, d.user_login)} style={btnStyle('var(--success)')}>Zatwierdź</button>
                          )}
                          {d.status === 'approved' && (
                            <button onClick={() => handleBlock(d.id)} style={btnStyle('var(--danger)')}>Zablokuj</button>
                          )}
                          {d.status === 'blocked' && (
                            <button onClick={() => openApproveModal(d.id, d.user_login)} style={btnStyle('var(--success)')}>Odblokuj</button>
                          )}
                          <button onClick={() => handleDelete(d.id)} style={btnStyle('var(--text-muted)')}>Usuń</button>
                          {d.device_name && (
                            <button onClick={() => { setEditingName(d.id); setNameValue(d.device_name || ''); }} style={btnStyle('var(--cyan)')}>Edytuj</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Widok: Historia logowań */}
      {activeView === 'attempts' && (
        <div style={cardStyle}>
          {loginAttempts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '13px' }}>
              Brak prób logowania
            </div>
          ) : (
            <div className="admin-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Data</th>
                    <th style={thStyle}>Użytkownik</th>
                    <th style={thStyle}>Typ</th>
                    <th className="col-hide-mobile" style={thStyle}>Wiadomość</th>
                    <th className="col-hide-mobile" style={thStyle}>IP</th>
                    <th className="col-hide-mobile" style={thStyle}>Urządzenie</th>
                  </tr>
                </thead>
                <tbody>
                  {loginAttempts.map(a => (
                    <tr key={a.id} style={{
                      backgroundColor: a.attempt_type === 'blocked_device' ? '#fffbe6'
                        : a.attempt_type === 'bad_password' || a.attempt_type === 'unknown_user' ? '#fff5f5'
                        : 'transparent'
                    }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '11px' }}>{formatDate(a.created_at)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{a.user_login}</td>
                      <td style={tdStyle}>{attemptBadge(a.attempt_type)}</td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontSize: '11px' }}>{a.message || '-'}</td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px' }}>{a.ip_address || '-'}</td>
                      <td className="col-hide-mobile" style={{ ...tdStyle, fontSize: '11px' }}>{parseUA(a.user_agent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Modal zatwierdzania */}
      {approveModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000
        }} onClick={() => setApproveModal(null)}>
          <div style={{
            backgroundColor: 'var(--card-bg, #fff)', borderRadius: '12px', padding: '20px',
            width: 'min(92vw, 440px)', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>
              Zatwierdz urzadzenie
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px' }}>
              Uzytkownik: <strong>{approveModal.userLogin}</strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" checked={approvePermanent} onChange={() => setApprovePermanent(true)} />
                Na stale (bez wygasniecia)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" checked={!approvePermanent} onChange={() => setApprovePermanent(false)} />
                Czasowe (do wybranej daty i godziny)
              </label>

              {!approvePermanent && (
                <input
                  type="datetime-local"
                  value={approveExpires}
                  onChange={e => setApproveExpires(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  style={{
                    padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border)',
                    borderRadius: '6px', width: '100%', boxSizing: 'border-box'
                  }}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setApproveModal(null)} style={{
                padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)',
                backgroundColor: 'var(--card-bg)', cursor: 'pointer', color: 'var(--text)'
              }}>
                Anuluj
              </button>
              <button onClick={handleApproveConfirm} style={{
                padding: '6px 12px', fontSize: '12px', border: 'none',
                backgroundColor: 'var(--success)', color: '#fff',
                cursor: 'pointer', fontWeight: 600
              }}>
                Zatwierdz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustedDevicesTab;
